/**
 * Subset of theme colors needed for field focus ring computation.
 * Callers can pass the full `useTheme().colors` object.
 */
type FieldFocusRingColors = Readonly<{
  inputBorder: string;
  inputBorderFocused: string;
  inputBorderError: string;
  inputFillDisabled: string;
  primary7: string;
  mono6: string;
}>;

type FieldFocusRingOptions = Readonly<{
  isInvalid?: boolean;
  isDisabled?: boolean;
  focusColor?: 'inputBorderFocused' | 'primary7';
}>;

type FieldFocusRingResult = Readonly<{
  /** Box-shadow for the resting (unfocused, unhovered) state. */
  resting: string;
  /** Box-shadow for the hovered state. */
  hover: string;
  /** Box-shadow for the focused state. */
  focus: string;
  /** Outline value for InputV1 container error indicator. */
  errorOutline: string;
  /** Box-shadow for InputV1's ::after focus ring overlay. */
  afterShadow: string;
}>;

/**
 * Token-based box-shadow values for field focus/hover/error/disabled states.
 * Replaces hardcoded #D9D9D9, #333, #c50020, #5f269b patterns.
 */
export function fieldFocusRing(colors: FieldFocusRingColors, options: FieldFocusRingOptions = {}): FieldFocusRingResult {
  const { isInvalid = false, isDisabled = false, focusColor = 'inputBorderFocused' } = options;

  const fc = focusColor === 'primary7' ? colors.primary7 : colors.inputBorderFocused;
  const hc = focusColor === 'primary7' ? colors.mono6 : colors.inputBorderFocused;

  if (isInvalid) {
    const err1 = `0px 0 0px 1px ${colors.inputBorderError}`;
    const err2 = `0px 0 0px 2px ${colors.inputBorderError}`;
    return { resting: err2, hover: err2, focus: err2, errorOutline: `2px solid ${colors.inputBorderError}`, afterShadow: err1 };
  }

  if (isDisabled) {
    const dis = `0px 0 0px 1px ${colors.inputBorder}`;
    return { resting: dis, hover: dis, focus: dis, errorOutline: '2px solid transparent', afterShadow: dis };
  }

  return {
    resting: `0px 0 0px 1px ${colors.inputBorder}`,
    hover: `0px 0 0px 1px ${hc}`,
    focus: `0px 0 0px 2px ${fc}`,
    errorOutline: '2px solid transparent',
    afterShadow: `0px 0 0px 1px ${fc}`,
  };
}

export type { FieldFocusRingColors, FieldFocusRingOptions, FieldFocusRingResult };
